import Form from "@/components/Form";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { DEFAULT_DATE_FORMAT } from "@/constants";
import { isDefined } from "@/lib/utils/isDefined";
import { useProjectSchedulesContext } from "@/modules/projects/components/schedules/ProjectSchedulesContext";
import { ProjectSchedulesDialogHeader } from "@/modules/projects/components/schedules/ProjectSchedulesDialogHeader";
import { ProjectSchedulesUpdateDialogFooter } from "@/modules/projects/components/schedules/ProjectSchedulesUpdateDialogFooter";
import { ProjectSchedulesUpdateDialogForm } from "@/modules/projects/components/schedules/ProjectSchedulesUpdateDialogForm";
import { ProjectSchedulesService } from "@/modules/projects/service/projectSchedule";
import { updateProjectScheduleForm } from "@/schemas/projectSchedule";
import { UpdateProjectScheduleForm } from "@/types/projectSchedule";
import { format } from "date-fns";
import { memo, useCallback, useEffect, useMemo } from "react";
import { useRowSelection } from "@/components/data-table/hooks/useRowSelection";
import { useTranslation } from "react-i18next";
import { convertUpdateProjectScheduleFormToUpdateProjectSchedule } from "./schema";
import {
  AsyncOperationProgress,
  OperationProgressStatus,
} from "@/components/progress/AsyncOperationProgress";
import { useProjectSchedulesUpsertFormWithBatch } from "@/modules/projects/hooks/useProjectSchedulesUpsertFormWithBatch";

const ProjectSchedulesUpdateDialogComponent = () => {
  const { t } = useTranslation();
  const {
    projectId,
    data,
    refetch,
    selectedStartDate,
    selectedEndDate,
    isUpdateDialogOpened,
    setIsUpdateDialogOpened,
  } = useProjectSchedulesContext();
  const { getSelection } = useRowSelection();

  const prepareData = useCallback(
    (values: UpdateProjectScheduleForm) => {
      return Array.from(getSelection())
        .map((scheduleId) => {
          const item = data.find(({ id }) => id === scheduleId);
          if (!item) {
            return null;
          }
          return convertUpdateProjectScheduleFormToUpdateProjectSchedule.parse({
            ...values,
            id: scheduleId,
            store_id: item.store_id,
            project_role_id: item.project_role_id,
          });
        })
        .filter(isDefined);
    },
    [getSelection, data],
  );

  const processBatch = useCallback(
    (batch: ReturnType<typeof prepareData>) => {
      return ProjectSchedulesService.updateSchedules(projectId, batch);
    },
    [projectId],
  );

  const onSuccess = useCallback(() => {
    refetch();
  }, [refetch]);

  const { form, defaultValues, mutation, operationProgress, handlers } =
    useProjectSchedulesUpsertFormWithBatch({
      startDate: selectedStartDate,
      endDate: selectedEndDate,
      schema: updateProjectScheduleForm,
      prepareData,
      processBatch,
      onSuccess,
    });

  const prefilledSettlementType = useMemo(() => {
    return data.find(({ id }) => id === Array.from(getSelection())[0])
      ?.settlement_type;
  }, [getSelection, data]);

  useEffect(() => {
    form.setValue("date_start", format(selectedStartDate, DEFAULT_DATE_FORMAT));
    form.setValue("date_end", format(selectedEndDate, DEFAULT_DATE_FORMAT));
  }, [form, selectedStartDate, selectedEndDate]);

  useEffect(() => {
    if (isUpdateDialogOpened && prefilledSettlementType) {
      form.setValue("settlement_type", prefilledSettlementType);
    }
  }, [isUpdateDialogOpened, prefilledSettlementType, form]);

  const onOpenChange = useCallback(
    (open: boolean) => {
      if (!open && operationProgress.isBusy()) {
        // Prevent closing dialog while operation is in progress
        return;
      }
      operationProgress.reset();
      setIsUpdateDialogOpened(open);
      form.reset({
        ...defaultValues,
        settlement_type: prefilledSettlementType,
      });
    },
    [
      defaultValues,
      form,
      setIsUpdateDialogOpened,
      operationProgress,
      prefilledSettlementType,
    ],
  );

  const handleClose = useCallback(() => {
    setIsUpdateDialogOpened(false);
  }, [setIsUpdateDialogOpened]);

  const isShowingProgress =
    operationProgress.status !== OperationProgressStatus.IDLE;

  return (
    <Dialog open={isUpdateDialogOpened} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[40dvw] min-w-[700px] max-h-[90dvh] overflow-y-auto">
        <ProjectSchedulesDialogHeader
          title={t("projectSchedules.dialog.update")}
        />
        {isShowingProgress ? (
          <AsyncOperationProgress
            operationProgress={operationProgress}
            currentItemLabel={operationProgress.currentItemLabel}
            translationKey="projectSchedules.progress"
            onCancel={handlers.cancel}
            onClose={handleClose}
          />
        ) : (
          <Form
            form={form}
            handleSubmit={mutation.mutateAsync}
            className="gap-6"
          >
            <ProjectSchedulesUpdateDialogForm />
            <ProjectSchedulesUpdateDialogFooter />
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
};
export const ProjectSchedulesUpdateDialog = memo(
  ProjectSchedulesUpdateDialogComponent,
);
